import React, { createRef, JSX, ReactNode, RefObject, useMemo, useState } from 'react';
import { ApiBestPricePackageModel } from '@ibe/api';
import SimpleList from './SimpleList';
import fallback from '../../Translations/generated/PackageSearch.de.json';
import Keys from '../../Translations/generated/PackageSearch.de.json.keys';
import { useTranslation } from '@ibe/components';
import { HeadItem, ListRow, ListRowItem, Sorting } from './types';
import { orderBy } from 'lodash-es';

export const sortFunctions: {
  [key: string]: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ) => ApiBestPricePackageModel[];
} = {
  startDate: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ): ApiBestPricePackageModel[] => {
    return orderBy(
      newPackages,
      [
        item => new Date(item.startDate),
        'originName',
        item => item.price.finalPrice,
        item => item.groupSize,
        item => item.availability
      ],
      sorting.isAscDirection
        ? ['asc', 'asc', 'asc', 'asc', 'asc']
        : ['desc', 'desc', 'desc', 'desc', 'desc']
    );
  },
  price: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ): ApiBestPricePackageModel[] => {
    return orderBy(
      newPackages,
      [
        item => item.price.finalPrice,
        item => new Date(item.startDate),
        'originName',
        item => item.groupSize,
        item => item.availability
      ],
      sorting.isAscDirection
        ? ['asc', 'asc', 'asc', 'asc', 'asc']
        : ['desc', 'desc', 'desc', 'desc', 'desc']
    );
  },
  availability: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ): ApiBestPricePackageModel[] => {
    return orderBy(
      newPackages,
      [
        item => item.availability,
        item => new Date(item.startDate),
        'originName',
        item => item.price.finalPrice,
        item => item.groupSize
      ],
      sorting.isAscDirection
        ? ['asc', 'asc', 'asc', 'asc', 'asc']
        : ['desc', 'desc', 'desc', 'desc', 'desc']
    );
  },
  origin: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ): ApiBestPricePackageModel[] => {
    return orderBy(
      newPackages,
      [
        'originName',
        item => new Date(item.startDate),
        item => item.price.finalPrice,
        item => item.groupSize,
        item => item.availability
      ],
      sorting.isAscDirection
        ? ['asc', 'asc', 'asc', 'asc', 'asc']
        : ['desc', 'desc', 'desc', 'desc', 'desc']
    );
  },
  smallGroup: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ): ApiBestPricePackageModel[] => {
    return orderBy(
      newPackages,
      [
        item => item.groupSize === 'smallGroup',
        item => new Date(item.startDate),
        'originName',
        item => item.price.finalPrice,
        item => item.availability
      ],
      sorting.isAscDirection
        ? ['asc', 'asc', 'asc', 'asc', 'asc']
        : ['desc', 'desc', 'desc', 'desc', 'desc']
    );
  },
  miniGroup: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ): ApiBestPricePackageModel[] => {
    return orderBy(
      newPackages,
      [
        item => item.groupSize === 'miniGroup',
        item => new Date(item.startDate),
        'originName',
        item => item.price.finalPrice,
        item => item.availability
      ],
      sorting.isAscDirection
        ? ['asc', 'asc', 'asc', 'asc', 'asc']
        : ['desc', 'desc', 'desc', 'desc', 'desc']
    );
  }
};

export const getSortedPackages = (
  newPackages: ApiBestPricePackageModel[],
  sorting: Sorting
): ApiBestPricePackageModel[] => {
  return sortFunctions[sorting.sortProp]
    ? sortFunctions[sorting.sortProp](newPackages, sorting)
    : [];
};

const PackageSimpleListWrapper = ({
  packages,
  onSelect,
  headItems,
  customGetSortedPackages,
  packagesMappingCallback,
  initialSortProp,
  initialIsAscDirection,
  numberOfDisplayedItems,
  numberOfInitiallyDisplayedItems,
  staticNonTableContent,
  checkAvailability,
  errorRowColSpanDiff
}: {
  packages: ApiBestPricePackageModel[];
  onSelect: (id: string, code: string, groupSize?: string, index?: number) => void;
  headItems?: Array<HeadItem>;
  customGetSortedPackages?: (
    newPackages: ApiBestPricePackageModel[],
    sorting: Sorting
  ) => ApiBestPricePackageModel[];
  packagesMappingCallback?: (
    singlePackage: ApiBestPricePackageModel,
    index?: number
  ) => ListRow<ListRowItem>;
  initialSortProp?: string;
  initialIsAscDirection?: boolean;
  numberOfDisplayedItems?: number;
  numberOfInitiallyDisplayedItems?: number;
  staticNonTableContent?: ReactNode;
  checkAvailability?: (
    id: string,
    code: string,
    groupSize?: string,
    index?: number
  ) => Promise<boolean>;
  errorRowColSpanDiff?: number;
}): JSX.Element => {
  const { t } = useTranslation('PackageSearch', fallback, Keys);

  const localHeadItems = useMemo(() => {
    return (
      headItems || [
        { value: t(Keys.date), sortProp: 'startDate' },
        { value: '', nestedSortProp: 'isSmallGroup' },
        { value: '', nestedSortProp: 'isMiniGroup' },
        { value: t(Keys.origin), sortProp: 'origin' },
        { value: t(Keys.pricePerPerson), sortProp: 'price' }
      ]
    );
  }, [headItems]);

  const [sorting, setSorting] = useState<Sorting>({
    sortProp: initialSortProp ?? 'startDate',
    isAscDirection: initialIsAscDirection ?? true
  });

  const sortedLocalMappedPackages = useMemo(() => {
    return (customGetSortedPackages
      ? customGetSortedPackages(packages, sorting)
      : getSortedPackages(packages, sorting)
    ).map(
      packagesMappingCallback ??
        (singlePackage => {
          return {
            id: singlePackage.id,
            code: singlePackage.code,
            groupSize: singlePackage?.groupSize ?? '',
            nodeRef: createRef<HTMLElement | undefined>() as RefObject<HTMLElement>,
            item: {
              startDate: {
                value: singlePackage.startDate ?? ''
              },
              duration: {
                value: singlePackage.duration.duration ?? '',
                hideInDesktop: true
              },
              origin: {
                value: singlePackage.originName ?? ''
              },
              price: {
                value: singlePackage.price.finalPrice ?? 0
              }
            }
          };
        })
    );
  }, [customGetSortedPackages, sorting, packages, packagesMappingCallback]);

  return (
    <div>
      <SimpleList
        listRows={sortedLocalMappedPackages}
        headItems={localHeadItems}
        onSelect={onSelect}
        onSort={setSorting}
        controlledSorting={sorting}
        itemsInRowInMobile={2}
        numberOfDisplayedItems={numberOfDisplayedItems}
        numberOfInitiallyDisplayedItems={numberOfInitiallyDisplayedItems}
        staticNonTableContent={staticNonTableContent}
        checkAvailability={checkAvailability}
        errorRowColSpanDiff={errorRowColSpanDiff}
      />
    </div>
  );
};

export default PackageSimpleListWrapper;
